import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Table, Input, Select, Button, Badge, Field, SortControl, sortRows } from "@/lib/ui";

const TYPES = ["Category", "Product", "Keyword", "Brand", "Material", "Other"];

export default function RestrictedItems() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ term: "", type: "Keyword", rule: "Block Immediately" });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "term", dir: "asc" });
  const SORT_FIELDS = [
    { key: "term", label: "Term", type: "str" },
    { key: "type", label: "Type", type: "str" },
    { key: "rule", label: "Rule", type: "str" },
    { key: "status", label: "Status", type: "str" },
  ];

  const load = () => api.get("/admin/restricted-items").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.term.trim()) return;
    try { await api.post("/admin/restricted-items", form); toast.success("Restriction added"); setForm({ term: "", type: "Keyword", rule: "Block Immediately" }); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (r) => {
    try { await api.delete(`/admin/restricted-items/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/restricted-items/${editing.id}`, {
        term: editing.term, type: editing.type, rule: editing.rule, status: editing.status || "active",
      });
      toast.success("Restriction updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = rows.filter((r) => r.term.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Restricted Items Planner</h1>
      <p className="mt-1 text-sm text-slate-500">Food is permanently locked and cannot be modified or deleted.</p>

      <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-[200px] flex-1">
          <p className="mb-1 text-xs font-bold uppercase text-slate-500">Restricted Term</p>
          <Input value={form.term} onChange={(e) => setForm((f) => ({ ...f, term: e.target.value }))} placeholder="e.g. alcohol" data-testid="ri-term" />
        </div>
        <Select className="w-auto" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="ri-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</Select>
        <Select className="w-auto" value={form.rule} onChange={(e) => setForm((f) => ({ ...f, rule: e.target.value }))} data-testid="ri-rule"><option>Block Immediately</option><option>Flag for Admin Review</option></Select>
        <Button type="submit" data-testid="ri-add"><i className="fa-solid fa-plus" /> Add</Button>
      </form>

      <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder="Search restrictions…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ri-search" />
        <SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="ri-sort" />
      </div>

      <Table head={["Term", "Type", "Rule", "Status", "Created By", "Actions"]}>
        {sortRows(filtered, SORT_FIELDS, sort).map((r) => (
          <tr key={r.id} data-testid={`ri-row-${r.id}`}>
            <td className="px-4 py-3 font-semibold text-slate-800">{r.term}</td>
            <td className="px-4 py-3">{r.type}</td>
            <td className="px-4 py-3">{r.rule}</td>
            <td className="px-4 py-3">
              {r.locked ? <Badge tone="red"><i className="fa-solid fa-lock" /> LOCKED / PERMANENT</Badge> : <Badge tone={r.status === "active" ? "green" : "slate"}>{r.status}</Badge>}
            </td>
            <td className="px-4 py-3 text-slate-500">{r.created_by}</td>
            <td className="px-4 py-3">
              {r.locked ? <span className="text-xs text-slate-400">Non-removable</span> : (
                <div className="flex gap-1">
                  <Button variant="outline" onClick={() => setEditing({ ...r, status: r.status || "active" })} data-testid={`ri-edit-${r.id}`}><i className="fa-solid fa-pen" /></Button>
                  <Button variant="ghost" onClick={() => del(r)} data-testid={`ri-del-${r.id}`}><i className="fa-solid fa-trash text-red-500" /></Button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </Table>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="ri-edit-modal">
            <h2 className="font-head text-xl font-extrabold text-slate-900">Edit Restriction</h2>
            <div className="mt-4 space-y-3">
              <Field label="Restricted Term"><Input value={editing.term} onChange={(e) => setEditing((s) => ({ ...s, term: e.target.value }))} data-testid="ri-edit-term" /></Field>
              <Field label="Type"><Select value={editing.type} onChange={(e) => setEditing((s) => ({ ...s, type: e.target.value }))}>{TYPES.map((t) => <option key={t}>{t}</option>)}</Select></Field>
              <Field label="Rule"><Select value={editing.rule} onChange={(e) => setEditing((s) => ({ ...s, rule: e.target.value }))}><option>Block Immediately</option><option>Flag for Admin Review</option></Select></Field>
              <Field label="Status"><Select value={editing.status} onChange={(e) => setEditing((s) => ({ ...s, status: e.target.value }))}><option value="active">active</option><option value="inactive">inactive</option></Select></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} data-testid="ri-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
